import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import Container from "@material-ui/core/Container";
import { Card, Chip, IconButton, Tooltip, Typography } from "@material-ui/core";
import ContentCopyIcon from "@material-ui/icons/FileCopy";
import { useSelector } from "react-redux";
import { IAppState } from "../../store";
import { GET_FEATURED_OFFERS } from "../../graphql/offers/getFeaturedOffersQuery";
import { getSignedUrl } from "../../utils/getSignedUrl";
import { toCurrency } from "../utils";
import { useTranslation } from "react-i18next";
import styles from "./index.module.css";
import offerPlaceholder from "../../assets/images/offer-placeholder.jpg";

interface IFeaturedOfferDetails {
  id: string;
  name: string;
  code: string;
  discountValueType: string;
  discountValue: number;
  maxDiscountCap?: number;
}

const FeaturedOffersModule: React.FC = () => {
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { currency, locale } = website.organisation;
  const { t } = useTranslation();
  const [imageUrls, setImageUrls] = useState<{ [offerId: string]: string }>({});

  const [getFeaturedOffers, { data }] = useLazyQuery(GET_FEATURED_OFFERS, {
    fetchPolicy: "network-only"
  });

  const featuredOffers = website.featuredOffers || [];

  useEffect(() => {
    if (featuredOffers.length) {
      getFeaturedOffers({ variables: { ids: featuredOffers.map((f) => f.offerId) } });
      Promise.all(
        featuredOffers
          .filter((f) => f.imageUrl)
          .map(async (f) => ({ offerId: f.offerId, url: await getSignedUrl(f.imageUrl) }))
      ).then((resolved) => {
        setImageUrls(
          resolved.reduce((acc, r) => ({ ...acc, [r.offerId]: r.url }), {} as { [offerId: string]: string })
        );
      });
    }
    // website.featuredOffers is a fresh array reference from the reducer on
    // every website fetch - compare by content, not the array id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(featuredOffers)]);

  const offers: IFeaturedOfferDetails[] = data?.consumerGetFeaturedOffers || [];

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  const discountLabel = (offer: IFeaturedOfferDetails) => {
    if (offer.discountValueType === "PERCENTAGE") {
      return `${offer.discountValue}% ${t("root:off")}`;
    }
    return `${toCurrency(offer.discountValue * 100, currency, locale)} ${t("root:off")}`;
  };

  if (!offers.length) {
    return null;
  }

  return (
    <div className={styles.featuredOffersModule}>
      <Container>
        <div className="flex col-flex cross-center">
          <div className={styles.sectionHeaderLine}></div>
          <h4>{t("root:availableOffers")}</h4>
        </div>
        <div className={styles.offersContainer}>
          {offers.map((offer) => (
            <Card key={offer.id} className={styles.offerCard}>
              <img src={imageUrls[offer.id] || offerPlaceholder} alt={offer.name} className={styles.offerImage} />
              <div className={styles.offerBody}>
                <Typography variant="h5">{offer.name}</Typography>
                <Chip label={discountLabel(offer)} color="primary" size="small" className={styles.offerChip} />
                <div className={styles.offerCode}>
                  <Typography variant="body2">{offer.code}</Typography>
                  <Tooltip title={t("root:copyCode")}>
                    <IconButton size="small" onClick={() => copyCode(offer.code)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default FeaturedOffersModule;
